import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { collection, doc, getDocs, orderBy, query, serverTimestamp, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { useAuth } from '../../auth/AuthProvider';
import { useConfirm } from '../../ui/ConfirmProvider';
import {
  ADMIN_DEFAULT_PAGE_KEYS,
  ONLINE_DEFAULT_PAGE_KEYS,
  ONTIME_PAGE_DEFS,
  formatOnTimeRoleLabel,
  normalizeOnTimeRole,
} from '../utils/access';
import { readLinkProfileSession, writeLinkProfileSession } from '../auth/session';
import { getActiveStoreNames, loadOnTimeSettings } from '../lib/ontimeSettings';
import '../styles/linkProfilesRevamp.css';

const cardClass = 'rounded-3xl border border-white/10 bg-white/5 p-5';
const inputClass = 'link-profile-input w-full rounded-2xl border border-white/10 bg-[#0b1727] px-4 py-3 text-sm text-white outline-none placeholder:text-slate-500 focus:border-cyan-300/40';
const buttonClass = 'rounded-2xl border border-cyan-400/20 bg-cyan-500/10 px-4 py-2 text-sm text-cyan-100 hover:bg-cyan-500/15 disabled:cursor-not-allowed disabled:opacity-50';
const dangerButtonClass = 'rounded-2xl border border-rose-400/20 bg-rose-500/10 px-4 py-2 text-sm text-rose-100 hover:bg-rose-500/15 disabled:cursor-not-allowed disabled:opacity-50';
const DEFAULT_LINK_ADMIN_PIN = '000000';

const ROLE_OPTIONS = [
  { value: 'owner', label: 'Owner' },
  { value: 'admin', label: 'Admin' },
  { value: 'online-team', label: 'Online Team' },
  { value: 'store', label: 'Store' },
  { value: 'driver', label: 'Driver' },
];

const PAGE_OPTIONS = ONTIME_PAGE_DEFS.filter((page) => page.key !== 'profiles');

function cleanPin(value = '') {
  return String(value || '').replace(/\D/g, '').slice(0, 6);
}

function splitEmailName(email = '') {
  const local = String(email || '').split('@')[0] || 'Link Admin';
  const cleaned = local.replace(/[._-]+/g, ' ').replace(/\s+/g, ' ').trim();
  return cleaned ? cleaned.replace(/\b\w/g, (letter) => letter.toUpperCase()) : 'Link Admin';
}

function emptyForm() {
  return {
    fullName: '',
    surname: '',
    pin: '',
    role: 'online-team',
    assignedStores: [],
    onTimePages: [...ONLINE_DEFAULT_PAGE_KEYS],
  };
}

function getRolePages(role, pages) {
  const normalizedRole = normalizeOnTimeRole(role);
  if (normalizedRole === 'owner') return ONTIME_PAGE_DEFS.map((page) => page.key);
  if (normalizedRole === 'admin') return Array.from(new Set((pages || []).filter(Boolean)));
  if (normalizedRole === 'online-team') return Array.from(new Set((pages || []).filter(Boolean).filter((page) => page !== 'profiles')));
  if (normalizedRole === 'store') return ['store-queue'];
  if (normalizedRole === 'driver') return ['driver-view'];
  return [];
}

function profileName(profile = {}) {
  return String(profile.displayName || `${profile.fullName || ''} ${profile.surname || ''}`.trim() || profile.name || 'Link profile').trim();
}

function normalizeAssignedStores(value) {
  if (Array.isArray(value)) return Array.from(new Set(value.map((item) => String(item || '').trim()).filter(Boolean))).sort();
  const single = String(value || '').trim();
  return single ? [single] : [];
}

function profileStores(profile = {}) {
  return normalizeAssignedStores(
    profile.assignedStores || profile.stores || profile.storeAccess || profile.storeNames || profile.store || profile.storeName || profile.site || profile.siteName || profile.assignedStore
  );
}

function profileToSessionClaims(baseClaims = {}, profile = {}) {
  const role = normalizeOnTimeRole(profile.onTimeRole || profile.role || baseClaims?.onTimeRole || baseClaims?.role || 'online-team');
  const pages = getRolePages(role, profile.onTimePages || profile.allowedPages || profile.tabsAllowed || []);
  const displayName = profileName(profile);
  return {
    ...(baseClaims || {}),
    displayName,
    fullName: String(profile.fullName || displayName || '').trim(),
    surname: String(profile.surname || '').trim(),
    role,
    onTimeRole: role,
    onTimePages: pages,
    allowedPages: pages,
    tabsAllowed: pages,
    linkProfileId: profile.id,
    linkProfileName: displayName,
    orgAdmin: role === 'owner' || role === 'admin',
    canManageOnTimeUsers: role === 'owner' || role === 'admin',
  };
}

function mapProfile(snapshotId, data = {}) {
  const role = normalizeOnTimeRole(data.onTimeRole || data.role || 'online-team');
  const pin = cleanPin(data.accessPin || data.pin || data.linkPin || data.onTimePin);
  return {
    id: snapshotId,
    uid: String(data.uid || data.authUid || '').trim(),
    authUid: String(data.authUid || data.uid || '').trim(),
    fullName: String(data.fullName || data.firstName || data.displayName || data.name || '').trim(),
    surname: String(data.surname || data.lastName || '').trim(),
    displayName: String(data.displayName || data.name || '').trim(),
    email: String(data.email || '').trim().toLowerCase(),
    ownerUid: String(data.ownerUid || '').trim(),
    profileType: String(data.profileType || '').trim(),
    role,
    pin,
    active: data.active !== false && data.archived !== true && data.removed !== true && data.disabled !== true,
    archived: data.archived === true || data.removed === true || data.disabled === true,
    isDefaultAdmin: data.profileType === 'link-access-layer-admin' || data.profileType === 'link-auth-admin' || data.isDefaultAdmin === true,
    isAuthAdminProfile: data.isAuthAdminProfile === true || data.profileType === 'link-auth-admin',
    assignedStores: profileStores(data),
    onTimePages: getRolePages(role, data.onTimePages || data.allowedPages || data.tabsAllowed || []),
  };
}

function PermissionPicker({ role, value, onChange }) {
  const normalizedRole = normalizeOnTimeRole(role);
  if (normalizedRole === 'owner') return <div className="text-xs text-slate-400">Owner always has full Link access.</div>;
  if (normalizedRole === 'store') return <div className="text-xs text-slate-400">Store is fixed to Store Queue only.</div>;
  if (normalizedRole === 'driver') return <div className="text-xs text-slate-400">Driver is fixed to Driver View only.</div>;

  const selected = new Set(getRolePages(role, value));

  return (
    <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
      {PAGE_OPTIONS.map((page) => (
        <label key={page.key} className="flex items-center gap-2 rounded-2xl border border-white/10 bg-[#0b1727] px-3 py-2 text-sm text-slate-200">
          <input
            type="checkbox"
            checked={selected.has(page.key)}
            onChange={(e) => {
              const next = new Set(selected);
              if (e.target.checked) next.add(page.key);
              else next.delete(page.key);
              onChange(Array.from(next));
            }}
          />
          <span>{page.label}</span>
        </label>
      ))}
    </div>
  );
}

function StoreAccessPicker({ role, stores = [], value = [], onChange }) {
  const normalizedRole = normalizeOnTimeRole(role);
  if (!['store', 'driver'].includes(normalizedRole)) return null;
  const selected = new Set(normalizeAssignedStores(value));
  return (
    <div className="mt-3 rounded-2xl border border-cyan-400/15 bg-cyan-500/[0.04] p-3">
      <div className="mb-2 text-sm font-medium text-white">Assigned stores</div>
      <p className="mb-3 text-xs text-slate-400">{normalizedRole === 'store' ? 'Store PWA will only show orders for selected stores.' : 'Driver PWA will only show runs for selected stores.'}</p>
      {stores.length ? (
        <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
          {stores.map((store) => (
            <label key={store} className="flex items-center gap-2 rounded-2xl border border-white/10 bg-[#0b1727] px-3 py-2 text-sm text-slate-200 transition hover:border-cyan-300/30 hover:bg-cyan-400/[0.06]">
              <input
                type="checkbox"
                checked={selected.has(store)}
                onChange={(e) => {
                  const next = new Set(selected);
                  if (e.target.checked) next.add(store);
                  else next.delete(store);
                  onChange(Array.from(next).sort());
                }}
              />
              <span>{store}</span>
            </label>
          ))}
        </div>
      ) : (
        <div className="rounded-2xl border border-amber-400/20 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">No active stores found. Add stores in Link Settings first.</div>
      )}
    </div>
  );
}

function ProfileCard({ profile, display, isSelf, canRemove, editingId, editDraft, storeOptions, fieldSalt, beginEdit, archiveProfile, saveExistingProfile, cancelEdit, setEditDraft, applyRoleDefaults }) {
  return (
    <div className="rounded-3xl border border-cyan-400/10 bg-[#08111d]/90 p-5 shadow-2xl shadow-cyan-950/10">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <div className="text-base font-semibold text-white">{display}</div>
            <span className="rounded-full border border-emerald-400/20 bg-emerald-500/10 px-2.5 py-1 text-xs text-emerald-200">Active</span>
            <span className="rounded-full border border-cyan-400/20 bg-cyan-500/10 px-2.5 py-1 text-xs text-cyan-100">{formatOnTimeRoleLabel(profile.role)}</span>
            {isSelf ? <span className="rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-xs text-slate-300">Current profile</span> : null}
            {profile.isDefaultAdmin || profile.isAuthAdminProfile ? <span className="rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-xs text-slate-300">Default admin</span> : null}
          </div>
          <div className="text-sm text-slate-400">PIN protected profile</div>
          {profileStores(profile).length ? (
            <div className="flex flex-wrap gap-2 pt-1">
              {profileStores(profile).map((store) => <span key={store} className="rounded-full border border-cyan-400/15 bg-cyan-500/10 px-2.5 py-1 text-xs text-cyan-100">{store}</span>)}
            </div>
          ) : null}
          <div className="flex flex-wrap gap-2 pt-1">
            {getRolePages(profile.role, profile.onTimePages).map((pageKey) => {
              const page = ONTIME_PAGE_DEFS.find((item) => item.key === pageKey);
              return page ? <span key={page.key} className="rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-xs text-slate-300">{page.label}</span> : null;
            })}
          </div>
        </div>

        <div className="flex flex-wrap gap-2 xl:justify-end">
          <button type="button" className={buttonClass} onClick={() => beginEdit(profile)}>Edit</button>
          {canRemove ? <button type="button" className={dangerButtonClass} onClick={() => archiveProfile(profile)}>Remove</button> : null}
        </div>
      </div>

      {editingId === profile.id && editDraft ? (
        <div className="mt-4 rounded-2xl border border-cyan-400/20 bg-cyan-500/5 p-4">
          <div className="mb-3 text-sm font-medium text-cyan-100">Edit profile</div>
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
            <input className={inputClass} name={`${fieldSalt}-edit-name`} placeholder="Full name" value={editDraft.fullName} onChange={(e) => setEditDraft((prev) => ({ ...prev, fullName: e.target.value }))} autoComplete="off" data-lpignore="true" data-1p-ignore="true" data-form-type="other" readOnly onFocus={(e) => e.currentTarget.removeAttribute('readonly')} />
            <input className={inputClass} name={`${fieldSalt}-edit-surname`} placeholder="Surname" value={editDraft.surname} onChange={(e) => setEditDraft((prev) => ({ ...prev, surname: e.target.value }))} autoComplete="off" data-lpignore="true" data-1p-ignore="true" data-form-type="other" readOnly onFocus={(e) => e.currentTarget.removeAttribute('readonly')} />
            <input className={inputClass} name={`${fieldSalt}-edit-pin`} placeholder="6-digit PIN" value={editDraft.pin} onChange={(e) => setEditDraft((prev) => ({ ...prev, pin: cleanPin(e.target.value) }))} inputMode="numeric" type="password" maxLength={6} autoComplete="new-password" data-lpignore="true" data-1p-ignore="true" data-form-type="other" readOnly onFocus={(e) => e.currentTarget.removeAttribute('readonly')} />
            <select className={inputClass} value={editDraft.role} onChange={(e) => setEditDraft((prev) => ({ ...prev, role: e.target.value, onTimePages: applyRoleDefaults(e.target.value) }))}>
              {ROLE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
          </div>
          <div className="mt-3">
            <div className="mb-2 text-sm font-medium text-white">Page access</div>
            <PermissionPicker role={editDraft.role} value={editDraft.onTimePages} onChange={(pages) => setEditDraft((prev) => ({ ...prev, onTimePages: pages }))} />
            <StoreAccessPicker role={editDraft.role} stores={storeOptions} value={editDraft.assignedStores} onChange={(assignedStores) => setEditDraft((prev) => ({ ...prev, assignedStores }))} />
          </div>
          <div className="mt-4 flex flex-wrap justify-end gap-2">
            <button type="button" className={buttonClass} onClick={() => saveExistingProfile(profile)}>Save changes</button>
            <button type="button" className={dangerButtonClass} onClick={cancelEdit}>Cancel</button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default function OnTimeProfilesPage() {
  const { user, claims, refreshClaims } = useAuth();
  const orgId = String(claims?.orgId || '').trim();
  const currentProfileId = String(claims?.linkProfileId || '').trim();
  const currentRole = normalizeOnTimeRole(claims?.onTimeRole || claims?.role || '');
  const canManage = Boolean(claims?.superAdmin || claims?.superadmin || claims?.orgAdmin || currentRole === 'owner' || currentRole === 'admin');
  const confirm = useConfirm();
  const fieldSalt = useMemo(() => `link-profiles-${String(user?.uid || user?.email || 'profile').replace(/[^a-z0-9]/gi, '')}-${Date.now()}`, [user?.uid, user?.email]);

  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [form, setForm] = useState(emptyForm());
  const [editingId, setEditingId] = useState('');
  const [editDraft, setEditDraft] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [storeOptions, setStoreOptions] = useState([]);

  const scrollTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  const defaultAdminPayload = useCallback(() => {
    const email = String(user?.email || claims?.email || '').trim().toLowerCase();
    const uid = String(user?.uid || claims?.uid || '').trim();
    const name = String(claims?.displayName || user?.displayName || splitEmailName(email)).trim();
    const pages = ONTIME_PAGE_DEFS.map((page) => page.key);
    return {
      id: uid ? `admin_${uid}` : email || 'link-admin',
      uid: uid || null,
      authUid: uid || null,
      ownerUid: uid || '',
      orgId,
      email,
      fullName: name,
      surname: '',
      displayName: name,
      accessPin: DEFAULT_LINK_ADMIN_PIN,
      pin: DEFAULT_LINK_ADMIN_PIN,
      linkPin: DEFAULT_LINK_ADMIN_PIN,
      role: 'admin',
      onTimeRole: 'admin',
      onTimePages: pages,
      allowedPages: pages,
      tabsAllowed: pages,
      active: true,
      archived: false,
      removed: false,
      disabled: false,
      apps: ['ontime'],
      defaultApp: 'ontime',
      shared: false,
      profileType: 'link-auth-admin',
      isDefaultAdmin: true,
      isAuthAdminProfile: true,
    };
  }, [claims?.displayName, claims?.email, claims?.uid, orgId, user?.displayName, user?.email, user?.uid]);

  const ensureDefaultAdmin = useCallback(async () => {
    if (!orgId || !user?.email) return null;
    const payload = defaultAdminPayload();
    const adminId = String(payload.id || (user?.uid ? `admin_${user.uid}` : payload.email || 'link-admin'));
    await setDoc(
      doc(db, 'orgs', orgId, 'linkProfiles', adminId),
      { ...payload, id: adminId, createdAt: serverTimestamp(), updatedAt: serverTimestamp() },
      { merge: true }
    );
    return { ...payload, id: adminId };
  }, [defaultAdminPayload, orgId, user?.email, user?.uid]);

  const loadProfiles = useCallback(async () => {
    if (!orgId) {
      setProfiles([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const currentUid = String(user?.uid || claims?.uid || '').trim();
      const expectedAdminId = currentUid ? `admin_${currentUid}` : '';
      const [snap, settings] = await Promise.all([
        getDocs(query(collection(db, 'orgs', orgId, 'linkProfiles'), orderBy('displayName'))),
        loadOnTimeSettings(orgId),
      ]);
      setStoreOptions(getActiveStoreNames(settings));
      let items = snap.docs
        .map((d) => mapProfile(d.id, d.data()))
        .filter((item) => item.active)
        // Each MatrixOS email gets one private admin profile. Do not show another admin email's private profile.
        .filter((item) => {
          const isPrivateAdmin = item.isAuthAdminProfile || item.isDefaultAdmin || item.profileType === 'link-auth-admin' || item.profileType === 'link-access-layer-admin';
          if (!isPrivateAdmin) return true;
          const ownerUid = String(item.ownerUid || item.authUid || item.uid || '').trim();
          return !ownerUid || ownerUid === currentUid || item.id === expectedAdminId || item.id === currentUid;
        });

      if (!items.some((item) => item.id === expectedAdminId || (item.isAuthAdminProfile && String(item.ownerUid || item.uid || '').trim() === currentUid))) {
        const adminProfile = await ensureDefaultAdmin();
        if (adminProfile) items = [mapProfile(adminProfile.id, adminProfile), ...items];
      }

      // Keep only one private admin profile for this logged-in MatrixOS account.
      const adminCandidates = items.filter((item) => item.isAuthAdminProfile || item.isDefaultAdmin || item.profileType === 'link-auth-admin' || item.profileType === 'link-access-layer-admin');
      const preferredAdmin = adminCandidates.find((item) => item.id === expectedAdminId)
        || adminCandidates.find((item) => String(item.ownerUid || item.authUid || item.uid || '').trim() === currentUid)
        || adminCandidates[0]
        || null;

      // Hide legacy/shared admin rows from the grouped list when the real default admin exists.
      items = items.filter((item) => {
        const isPrivateAdmin = item.isAuthAdminProfile || item.isDefaultAdmin || item.profileType === 'link-auth-admin' || item.profileType === 'link-access-layer-admin';
        if (isPrivateAdmin) return preferredAdmin && item.id === preferredAdmin.id;
        if (preferredAdmin && normalizeOnTimeRole(item.role) === 'admin') return false;
        return true;
      });

      items.sort((a, b) => {
        const aAdmin = a.isAuthAdminProfile || a.isDefaultAdmin;
        const bAdmin = b.isAuthAdminProfile || b.isDefaultAdmin;
        if (aAdmin && !bAdmin) return -1;
        if (!aAdmin && bAdmin) return 1;
        return profileName(a).localeCompare(profileName(b));
      });
      setProfiles(items);
    } catch (err) {
      console.error(err);
      setError(err?.message || 'Failed to load Link profiles.');
    } finally {
      setLoading(false);
    }
  }, [ensureDefaultAdmin, orgId, user?.uid]);

  useEffect(() => {
    loadProfiles();
  }, [loadProfiles]);

  function applyRoleDefaults(nextRole) {
    const normalized = normalizeOnTimeRole(nextRole);
    if (normalized === 'owner') return ONTIME_PAGE_DEFS.map((page) => page.key);
    if (normalized === 'admin') return [...ADMIN_DEFAULT_PAGE_KEYS];
    if (normalized === 'online-team') return [...ONLINE_DEFAULT_PAGE_KEYS];
    if (normalized === 'store') return ['store-queue'];
    if (normalized === 'driver') return ['driver-view'];
    return [];
  }

  function updateForm(patch) {
    setForm((prev) => {
      const next = { ...prev, ...patch };
      if (Object.prototype.hasOwnProperty.call(patch, 'role')) next.onTimePages = applyRoleDefaults(patch.role);
      if (Object.prototype.hasOwnProperty.call(patch, 'pin')) next.pin = cleanPin(patch.pin);
      return next;
    });
  }

  function beginEdit(profile) {
    setEditingId(profile.id);
    setEditDraft({
      fullName: profile.fullName,
      surname: profile.surname,
      pin: profile.pin,
      role: profile.role,
      assignedStores: profileStores(profile),
      onTimePages: [...profile.onTimePages],
    });
  }

  function cancelEdit() {
    setEditingId('');
    setEditDraft(null);
  }

  async function saveExistingProfile(profile) {
    if (!orgId || !editDraft) return;
    const nextRole = normalizeOnTimeRole(editDraft.role);
    const nextPages = getRolePages(nextRole, editDraft.onTimePages);
    const nextPin = cleanPin(editDraft.pin);
    if (nextPin.length !== 6) {
      setError('PIN must be exactly 6 digits.');
      scrollTop();
      return;
    }
    const nextStores = normalizeAssignedStores(editDraft.assignedStores);
    if (['store', 'driver'].includes(nextRole) && nextStores.length === 0) {
      setError('Select at least one store for this profile.');
      scrollTop();
      return;
    }
    try {
      const nextDisplayName = `${String(editDraft.fullName || '').trim()} ${String(editDraft.surname || '').trim()}`.trim();
      await updateDoc(doc(db, 'orgs', orgId, 'linkProfiles', profile.id), {
        fullName: String(editDraft.fullName || '').trim(),
        surname: String(editDraft.surname || '').trim(),
        displayName: nextDisplayName,
        accessPin: nextPin,
        pin: nextPin,
        linkPin: nextPin,
        role: nextRole,
        onTimeRole: nextRole,
        onTimePages: nextPages,
        allowedPages: nextPages,
        tabsAllowed: nextPages,
        assignedStores: nextStores,
        stores: nextStores,
        defaultApp: 'ontime',
        apps: ['ontime'],
        updatedAt: serverTimestamp(),
      });
      const activeSession = readLinkProfileSession();
      if (activeSession?.profileId === profile.id || profile.id === currentProfileId) {
        const nextProfile = {
          ...profile,
          fullName: String(editDraft.fullName || '').trim(),
          surname: String(editDraft.surname || '').trim(),
          displayName: nextDisplayName,
          role: nextRole,
          onTimeRole: nextRole,
          onTimePages: nextPages,
          allowedPages: nextPages,
          tabsAllowed: nextPages,
          assignedStores: nextStores,
          stores: nextStores,
        };
        writeLinkProfileSession({
          ...(activeSession || {}),
          orgId,
          profileId: profile.id,
          profileName: nextDisplayName,
          claims: profileToSessionClaims(activeSession?.claims || claims, nextProfile),
          updatedAt: Date.now(),
        });
      }
      setMessage('Profile updated.');
      cancelEdit();
      await loadProfiles();
      await refreshClaims({ force: false });
      scrollTop();
    } catch (err) {
      console.error(err);
      setError(err?.message || 'Could not update the Link profile.');
      scrollTop();
    }
  }

  async function createProfile(event) {
    event.preventDefault();
    if (!canManage || !orgId) return;

    const fullName = String(form.fullName || '').trim();
    const surname = String(form.surname || '').trim();
    const pin = cleanPin(form.pin);
    const role = normalizeOnTimeRole(form.role);
    const onTimePages = getRolePages(role, form.onTimePages);
    const assignedStores = normalizeAssignedStores(form.assignedStores);

    if (!fullName || !surname || pin.length !== 6) {
      setError('Please complete full name, surname, and a 6-digit PIN.');
      scrollTop();
      return;
    }
    if (['store', 'driver'].includes(role) && assignedStores.length === 0) {
      setError('Select at least one store for this profile.');
      scrollTop();
      return;
    }

    setSaving(true);
    setMessage('');
    setError('');

    try {
      const profileId = `link_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      await setDoc(doc(db, 'orgs', orgId, 'linkProfiles', profileId), {
        id: profileId,
        orgId,
        fullName,
        surname,
        displayName: `${fullName} ${surname}`.trim(),
        accessPin: pin,
        pin,
        linkPin: pin,
        role,
        onTimeRole: role,
        onTimePages,
        allowedPages: onTimePages,
        tabsAllowed: onTimePages,
        assignedStores,
        stores: assignedStores,
        active: true,
        shared: true,
        isAuthAdminProfile: false,
        archived: false,
        removed: false,
        disabled: false,
        defaultApp: 'ontime',
        apps: ['ontime'],
        profileType: 'link-pin-profile',
        createdByUid: String(user?.uid || ''),
        createdByEmail: String(user?.email || '').trim().toLowerCase(),
        updatedAt: serverTimestamp(),
        createdAt: serverTimestamp(),
      });
      setMessage('Link PIN profile created.');
      setForm(emptyForm());
      await loadProfiles();
      scrollTop();
    } catch (err) {
      console.error(err);
      setError(err?.message || 'Failed to create Link profile.');
      scrollTop();
    } finally {
      setSaving(false);
    }
  }

  async function archiveProfile(profile) {
    if (!orgId) return;
    if (profile.id === currentProfileId || profile.isDefaultAdmin) return;
    const ok = await confirm({
      title: 'Remove Link profile?',
      message: `Remove ${profileName(profile)} from the Link access layer?`,
      confirmText: 'Remove profile',
      cancelText: 'Cancel',
      variant: 'danger',
    });
    if (!ok) return;
    setError('');
    setMessage('');
    try {
      await updateDoc(doc(db, 'orgs', orgId, 'linkProfiles', profile.id), {
        active: false,
        archived: true,
        removed: true,
        disabled: true,
        archivedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      setMessage('Link profile removed.');
      await loadProfiles();
      scrollTop();
    } catch (err) {
      console.error(err);
      setError(err?.message || 'Could not remove the Link profile.');
      scrollTop();
    }
  }

  const privateAdminProfiles = profiles.filter((profile) => profile.isAuthAdminProfile || profile.isDefaultAdmin || profile.profileType === 'link-auth-admin' || profile.profileType === 'link-access-layer-admin');
  const groupedProfiles = profiles
    .filter((profile) => !(profile.isAuthAdminProfile || profile.isDefaultAdmin || profile.profileType === 'link-auth-admin' || profile.profileType === 'link-access-layer-admin'))
    .filter((profile) => normalizeOnTimeRole(profile.role) !== 'admin')
    .reduce((groups, profile) => {
      const label = formatOnTimeRoleLabel(profile.role);
      if (!groups[label]) groups[label] = [];
      groups[label].push(profile);
      return groups;
    }, {});
  const groupedEntries = Object.entries(groupedProfiles).sort(([a], [b]) => a.localeCompare(b));
  const toggleGroup = (groupName) => setExpandedGroups((prev) => ({ ...prev, [groupName]: !prev[groupName] }));

  if (!canManage) {
    return (
      <div className="link-revamp-shell space-y-6">
        <div>
          <h2 className="text-2xl font-semibold text-white">Profiles</h2>
          <p className="mt-1 text-sm text-slate-400">Owner-managed PIN profiles and role access for Link.</p>
        </div>
        <div className={`${cardClass} link-glow-card`}>
          <div className="rounded-2xl border border-amber-400/20 bg-amber-500/10 px-4 py-3 text-sm text-amber-200">
            Only the owner, admin, or super admin can manage Link profiles.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="link-revamp-shell space-y-6">
      <style>{`
        .link-profile-input:-webkit-autofill,
        .link-profile-input:-webkit-autofill:hover,
        .link-profile-input:-webkit-autofill:focus,
        .link-profile-input:-webkit-autofill:active {
          -webkit-text-fill-color: #ffffff !important;
          caret-color: #ffffff !important;
          box-shadow: 0 0 0 1000px #0b1727 inset !important;
          -webkit-box-shadow: 0 0 0 1000px #0b1727 inset !important;
          background-color: #0b1727 !important;
          background-image: none !important;
          transition: background-color 999999s ease-in-out 0s !important;
        }
      `}</style>
      <div>
        <h2 className="text-2xl font-semibold text-white">Profiles</h2>
        <p className="mt-1 text-sm text-slate-400">Create PIN profiles, control page access, and keep Link owner-managed.</p>
      </div>

      {message ? <div className="rounded-2xl border border-cyan-400/20 bg-cyan-500/10 px-4 py-3 text-sm text-cyan-100">{message}</div> : null}
      {error ? <div className="rounded-2xl border border-rose-400/20 bg-rose-500/10 px-4 py-3 text-sm text-rose-100">{error}</div> : null}

      <section className={`${cardClass} link-glow-card`}>
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-white"><span className='link-section-title'>Link Profile Builder</span><div className='mt-2'>Create Link profile</div></h3>
          <p className="mt-1 text-sm text-slate-400">Profiles unlock Link with a 6-digit PIN. The MatrixOS login email stays shared at the account level.</p>
        </div>

        <form className="space-y-4" onSubmit={createProfile} autoComplete="off" data-form-type="other">
          <input type="text" name={`${fieldSalt}-decoy-user`} className="pointer-events-none fixed -left-[9999px] -top-[9999px] h-0 w-0 opacity-0" tabIndex="-1" autoComplete="off" aria-hidden="true" />
          <input type="password" name={`${fieldSalt}-decoy-pass`} className="pointer-events-none fixed -left-[9999px] -top-[9999px] h-0 w-0 opacity-0" tabIndex="-1" autoComplete="new-password" aria-hidden="true" />
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <input className={inputClass} name={`${fieldSalt}-fullName`} placeholder="Full name" value={form.fullName} onChange={(e) => updateForm({ fullName: e.target.value })} autoComplete="new-password" data-lpignore="true" data-1p-ignore="true" data-form-type="other" readOnly onFocus={(e) => e.currentTarget.removeAttribute('readonly')} />
            <input className={inputClass} name={`${fieldSalt}-surname`} placeholder="Surname" value={form.surname} onChange={(e) => updateForm({ surname: e.target.value })} autoComplete="new-password" data-lpignore="true" data-1p-ignore="true" data-form-type="other" readOnly onFocus={(e) => e.currentTarget.removeAttribute('readonly')} />
            <input className={inputClass} name={`${fieldSalt}-pin`} placeholder="6-digit PIN" value={form.pin} onChange={(e) => updateForm({ pin: e.target.value })} inputMode="numeric" type="password" maxLength={6} autoComplete="new-password" data-lpignore="true" data-1p-ignore="true" data-form-type="other" readOnly onFocus={(e) => e.currentTarget.removeAttribute('readonly')} />
            <select className={inputClass} value={form.role} onChange={(e) => updateForm({ role: e.target.value })}>
              {ROLE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
          </div>

          <div>
            <div className="mb-2 text-sm font-medium text-white">Page access</div>
            <PermissionPicker role={form.role} value={form.onTimePages} onChange={(pages) => updateForm({ onTimePages: pages })} />
            <StoreAccessPicker role={form.role} stores={storeOptions} value={form.assignedStores} onChange={(assignedStores) => updateForm({ assignedStores })} />
          </div>

          <div className="flex justify-end">
            <button type="submit" className={buttonClass} disabled={saving}>{saving ? 'Saving…' : 'Create profile'}</button>
          </div>
        </form>
      </section>

      <section className={`${cardClass} link-glow-card`}>
        <div className="mb-4 flex items-center justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold text-white"><span className='link-section-title'>Shared Dispatch Profiles</span><div className='mt-2'>Created profiles</div></h3>
            <p className="mt-1 text-sm text-slate-400">Shared Link PIN profiles plus the default admin profile.</p>
          </div>
          <button type="button" className={buttonClass} onClick={loadProfiles}>Refresh</button>
        </div>

        {loading ? (
          <div className="rounded-2xl border border-white/10 bg-[#08111d] px-4 py-6 text-sm text-slate-400">Loading profiles…</div>
        ) : profiles.length === 0 ? (
          <div className="rounded-2xl border border-white/10 bg-[#08111d] px-4 py-6 text-sm text-slate-400">No Link profiles yet.</div>
        ) : (
          <div className="space-y-5">
            {privateAdminProfiles.map((profile) => {
              const isSelf = profile.id === currentProfileId;
              const display = profileName(profile);
              return (
                <ProfileCard
                  key={profile.id}
                  profile={profile}
                  display={display}
                  isSelf={isSelf}
                  canRemove={false}
                  editingId={editingId}
                  editDraft={editDraft}
                  storeOptions={storeOptions}
                  fieldSalt={fieldSalt}
                  beginEdit={beginEdit}
                  archiveProfile={archiveProfile}
                  saveExistingProfile={saveExistingProfile}
                  cancelEdit={cancelEdit}
                  setEditDraft={setEditDraft}
                  applyRoleDefaults={applyRoleDefaults}
                />
              );
            })}

            {groupedEntries.map(([groupName, groupProfiles]) => {
              const expanded = expandedGroups[groupName] === true;
              return (
                <div key={groupName} className="space-y-3">
                  <button
                    type="button"
                    onClick={() => toggleGroup(groupName)}
                    className="flex w-full items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3 text-left transition hover:border-cyan-300/25 hover:bg-white/[0.06]"
                  >
                    <div className="text-xs font-semibold uppercase tracking-[0.28em] text-cyan-200/80">{groupName}</div>
                    <div className="h-px flex-1 bg-white/10" />
                    <div className="rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-xs text-slate-400">{groupProfiles.length}</div>
                    <div className="text-xs font-semibold text-slate-400">{expanded ? 'Collapse' : 'Expand'}</div>
                  </button>
                  {expanded ? groupProfiles.map((profile) => {
                    const isSelf = profile.id === currentProfileId;
                    const display = profileName(profile);
                    return (
                      <ProfileCard
                        key={profile.id}
                        profile={profile}
                        display={display}
                        isSelf={isSelf}
                        canRemove={!isSelf}
                        editingId={editingId}
                        editDraft={editDraft}
                        storeOptions={storeOptions}
                  fieldSalt={fieldSalt}
                        beginEdit={beginEdit}
                        archiveProfile={archiveProfile}
                        saveExistingProfile={saveExistingProfile}
                        cancelEdit={cancelEdit}
                        setEditDraft={setEditDraft}
                        applyRoleDefaults={applyRoleDefaults}
                      />
                    );
                  }) : null}
                </div>
              );
            })}
          </div>        )}
      </section>
    </div>
  );
}
